import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useParams, useHistory } from "react-router-dom";
import { Spinner } from "reactstrap";

import StockPriceChart from "../../components/backtest/StockPriceChart";
import PnLChart from "../../components/backtest/PnLChart";
import LadderView from "../../components/backtest/LadderView";
import PositionsPanel from "../../components/backtest/PositionsPanel";
import StrategyInfoPanel from "../../components/backtest/StrategyInfoPanel";
import { strategyLabel } from "../../constants/strategies";

const SPEEDS = [
  { label: "1×",  fps: 1  },
  { label: "3×",  fps: 3  },
  { label: "10×", fps: 10 },
  { label: "30×", fps: 30 },
];

// ── P&L summary bar ───────────────────────────────────────────────────────────

function PnLBar({ frame }) {
  if (!frame) return null;
  const p = frame.pnl || {};
  const total = p.cumulative || 0;
  const items = [
    { label: "Opts Realized", value: p.realized_options  || 0, color: "#6f42c1", note: "booked" },
    { label: "Stock",         value: p.unrealised_stock   || 0, color: "#3b82f6", note: "MtM" },
    { label: "Open Opts",     value: p.unrealised_options || 0, color: "#fd7e14", note: "MtM" },
  ];
  const premium = p.premium_collected || 0;
  return (
    <div>
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        {items.map(it => (
          <div key={it.label}>
            <div style={{ fontSize: 10, color: "#888" }}>
              {it.label} <span style={{ color: "#ccc", fontSize: 9 }}>{it.note}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: it.value >= 0 ? it.color : "#dc3545" }}>
              {it.value >= 0 ? "+" : "−"}${Math.round(Math.abs(it.value)).toLocaleString()}
            </div>
          </div>
        ))}
        <div style={{ borderLeft: "1px solid #e9ecef", paddingLeft: 14 }}>
          <div style={{ fontSize: 10, color: "#888" }}>Total P&L</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: total >= 0 ? "#28a745" : "#dc3545" }}>
            {total >= 0 ? "+" : "−"}${Math.round(Math.abs(total)).toLocaleString()}
          </div>
        </div>
      </div>
      {premium !== 0 && (
        <div style={{ fontSize: 10, color: "#aaa", marginTop: 5 }}>
          ℹ {premium >= 0 ? "+" : "−"}${Math.round(Math.abs(premium)).toLocaleString()} gross option income collected (already included above)
        </div>
      )}
    </div>
  );
}

// ── Summary card ──────────────────────────────────────────────────────────────

function SummaryCard({ frames }) {
  const last = frames[frames.length - 1] || {};
  const p    = last.pnl || {};
  const allEvents = frames.flatMap(f => (f.events || []).filter(e => e.action_type));

  const items = [
    { label: "Days Simulated",       value: frames.length,          fmt: n => `${n}` },
    { label: "Total Adjustments",    value: allEvents.length,        fmt: n => `${n}` },
    { label: "Opts Realized",        value: p.realized_options  || 0, fmt: dollar, note: "booked" },
    { label: "Stock MtM",            value: p.unrealised_stock  || 0, fmt: dollar, note: "unrealized" },
    { label: "Open Options MtM",     value: p.unrealised_options || 0, fmt: dollar, note: "unrealized" },
    { label: "Total P&L",            value: p.cumulative        || 0, fmt: dollar, large: true },
  ];
  const premium = p.premium_collected || 0;

  return (
    <div style={summaryCard}>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#2c3e50", marginBottom: 10 }}>
        Backtest Complete
      </div>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        {items.map(it => (
          <div key={it.label}>
            <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: "0.4px" }}>
              {it.label}
              {it.note && <span style={{ color: "#ccc", marginLeft: 4, fontWeight: 400 }}>({it.note})</span>}
            </div>
            <div style={{
              fontSize: it.large ? 18 : 14,
              fontWeight: it.large ? 700 : 600,
              color: typeof it.value === "number" && it.value < 0 ? "#dc3545"
                   : typeof it.value === "number" && it.value > 0 && it.label !== "Days Simulated" && it.label !== "Total Adjustments" ? "#28a745"
                   : "#2c3e50",
              marginTop: 2,
            }}>
              {it.fmt(it.value)}
            </div>
          </div>
        ))}
      </div>
      {premium !== 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: "#999", borderTop: "1px solid #e0e8e0", paddingTop: 8 }}>
          ℹ Gross option income collected: {dollar(premium)} — roll/close costs are netted into "Opts Realized" above.
        </div>
      )}
    </div>
  );
}

// ── Adjustments log ───────────────────────────────────────────────────────────

const ACTION_LABELS = {
  SELL_INITIAL_STRANGLE:     "Sell Call + Put (Strangle)",
  SELL_INITIAL_CALL:         "Sell Call Hedge",
  SELL_INITIAL_CSP:          "Sell Cash-Secured Put",
  ROLL_DOWN_CALL:            "Roll Down Call",
  ROLL_OUT_CALL:             "Roll Out Call",
  ROLLUP_CALL:               "Roll Up Call",
  ROLLUP_CSP:                "Roll Up CSP",
  BUY_STOCK:                 "Buy Stock (Ladder)",
  REBUILD_LADDER:            "Rebuild Ladder",
  SQUARE_OFF_ALL_OPTIONS:    "Square Off All Options",
  SQUARE_OFF_CALLS:          "Square Off Calls",
  SQUARE_OFF_CALL_AND_STOCK: "Square Off Call + Stock",
  SQUARE_OFF_STOCKS:         "Square Off Stock",
  SITUATION_BRIEFING:        "Situation Briefing",
};

const ACTION_DESC = {
  SELL_INITIAL_STRANGLE:     "Opens both option legs immediately — a call hedge and a cash-secured put — before any stock is purchased.",
  SELL_INITIAL_CALL:         "Sell the initial call hedge at the derived strike to start collecting premium.",
  SELL_INITIAL_CSP:          "Sell a cash-secured put below the stock price for additional income.",
  ROLL_DOWN_CALL:            "Roll the short call to a lower strike — stock has dropped, the current call is now deep ITM.",
  ROLL_OUT_CALL:             "Roll the call to the next expiry — DTE is low and premium has decayed.",
  ROLLUP_CALL:               "Close the deep-ITM short call and sell a higher-strike OTM call. Captures more extrinsic value.",
  ROLLUP_CSP:                "Roll the short put to a higher strike, closer to LTP, for more premium.",
  BUY_STOCK:                 "Buy the next tranche of shares per the pyramid ladder.",
  REBUILD_LADDER:            "Recomputes the buy-ladder from the current price after a fill or roll, so the next buy level reflects the new average.",
  SQUARE_OFF_ALL_OPTIONS:    "Close all open option positions for this symbol.",
  SQUARE_OFF_CALLS:          "Close all open call positions.",
  SQUARE_OFF_CALL_AND_STOCK: "Close the short call and sell all stock holdings to lock in net profit.",
  SQUARE_OFF_STOCKS:         "Sell the entire stock position.",
  SITUATION_BRIEFING:        "Price band breached. System evaluated rebalance options.",
};

const ACTION_COLORS = {
  SELL_INITIAL_STRANGLE:     { bg: "#e8f5e9", border: "#4caf50", text: "#1b5e20", icon: "📞📤" },
  SELL_INITIAL_CALL:         { bg: "#e8f5e9", border: "#4caf50", text: "#1b5e20", icon: "📞" },
  SELL_INITIAL_CSP:          { bg: "#e3f2fd", border: "#2196f3", text: "#0d47a1", icon: "📤" },
  ROLL_DOWN_CALL:            { bg: "#fff8e1", border: "#ff9800", text: "#e65100", icon: "↓" },
  ROLL_OUT_CALL:             { bg: "#fff8e1", border: "#ff9800", text: "#e65100", icon: "→" },
  ROLLUP_CALL:               { bg: "#fce4ec", border: "#e91e63", text: "#880e4f", icon: "↑" },
  ROLLUP_CSP:                { bg: "#fce4ec", border: "#e91e63", text: "#880e4f", icon: "↑" },
  BUY_STOCK:                 { bg: "#e8eaf6", border: "#3f51b5", text: "#1a237e", icon: "＋" },
  REBUILD_LADDER:            { bg: "#e8eaf6", border: "#3f51b5", text: "#1a237e", icon: "≡" },
  SQUARE_OFF_ALL_OPTIONS:    { bg: "#fde2e2", border: "#dc3545", text: "#721c24", icon: "✕" },
  SQUARE_OFF_CALLS:          { bg: "#fde2e2", border: "#dc3545", text: "#721c24", icon: "✕" },
  SQUARE_OFF_CALL_AND_STOCK: { bg: "#fde2e2", border: "#dc3545", text: "#721c24", icon: "✕" },
  SQUARE_OFF_STOCKS:         { bg: "#fde2e2", border: "#dc3545", text: "#721c24", icon: "✕" },
  SITUATION_BRIEFING:        { bg: "#f3e5f5", border: "#9c27b0", text: "#4a148c", icon: "ℹ" },
};

function actionStyle(type) {
  return ACTION_COLORS[type] || { bg: "#f8f9fa", border: "#dee2e6", text: "#495057", icon: "⚙" };
}

const META_SKIP = new Set(["type", "side", "ladder", "strategy_name", "action_queue_id"]);

// Certain nested metadata groups always represent a "before vs after" pair
// (e.g. current_call vs proposed_call) and must stay disambiguated even
// when their field names don't collide with anything else.
const ALWAYS_PREFIX_GROUPS = new Set(["current_call", "proposed_call"]);

// Some events (SELL_INITIAL_STRANGLE, ROLL_DOWN_CALL) nest their real numbers
// one level down inside sub-objects (call/csp/current_call/proposed_call).
// Flatten those into the top level so they're visible in the log row —
// arrays (e.g. the full ladder) are deliberately left out of this compact view.
function flattenMeta(metadata) {
  if (!metadata) return {};
  const flat = {};
  const groups = [];
  for (const [k, v] of Object.entries(metadata)) {
    if (v != null && typeof v === "object" && !Array.isArray(v)) groups.push([k, v]);
    else flat[k] = v;
  }
  for (const [groupKey, group] of groups) {
    const forcePrefix = ALWAYS_PREFIX_GROUPS.has(groupKey);
    for (const [k, v] of Object.entries(group)) {
      if (v != null && typeof v === "object") continue; // skip deeper nesting / arrays
      const collides = k in flat || groups.some(([gk, g]) => gk !== groupKey && k in g);
      const key = forcePrefix || collides ? `${groupKey}_${k}` : k;
      flat[key] = v;
    }
  }
  return flat;
}

function fmtMetaVal(k, v) {
  if (v == null) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  const lk = k.toLowerCase();
  if (lk.includes("price") || lk.includes("ltp") || lk.includes("strike") || lk.includes("avg"))
    return `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (lk.includes("pct") || lk.includes("percent"))
    return `${Number(v).toFixed(2)}%`;
  if (lk.includes("delta"))
    return Number(v).toFixed(3);
  if (typeof v === "number")
    return Number.isInteger(v) ? v.toString() : Number(v).toFixed(2);
  return String(v);
}

const NOTE_LABELS = {
  ROLL_ON_EBP_BREACH: "EBP Breach — Considered",
};

function AdjLogRow({ r, expanded, onToggle }) {
  const [hovered, setHovered] = useState(false);
  const st = r.isNote
    ? { bg: "#f8f9fa", border: "#adb5bd", text: "#6c757d", icon: "🔍" }
    : actionStyle(r.action_type);

  const allPairs = Object.entries(flattenMeta(r.metadata))
    .filter(([k, v]) => !META_SKIP.has(k) && !k.startsWith("_") && typeof v !== "object" && v != null && v !== "");

  const shortDetails = r.isNote
    ? r.noteText
    : allPairs
        .slice(0, 3)
        .map(([k, v]) => `${k.replace(/_/g, " ")}: ${fmtMetaVal(k, v)}`)
        .join("  ·  ");

  return (
    <React.Fragment>
      <tr
        onClick={onToggle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          borderBottom: expanded ? "none" : "1px solid #f0f0f0",
          cursor: "pointer",
          background: expanded ? "#f0f4ff" : hovered ? "#f9f9fb" : "transparent",
          transition: "background 0.1s",
          opacity: r.isNote ? 0.85 : 1,
        }}
      >
        <td style={td}>{r.date}</td>
        <td style={td}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: st.text, paddingLeft: 7, borderLeft: `3px dashed ${st.border}`, fontStyle: r.isNote ? "italic" : "normal" }}>
            {st.icon} {r.isNote ? (NOTE_LABELS[r.action_type] || `${r.action_type.replace(/_/g, " ")} — Considered`) : (ACTION_LABELS[r.action_type] || r.action_type)}
          </span>
        </td>
        <td style={{ ...td, color: "#888", fontSize: 11, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {shortDetails || "—"}
        </td>
        <td style={{ ...td, textAlign: "right" }}>
          <span style={{ fontWeight: 600, color: r.pnl >= 0 ? "#28a745" : "#dc3545" }}>
            {dollar(r.pnl)}
          </span>
          <span style={{ marginLeft: 10, color: "#bbb", fontSize: 11 }}>{expanded ? "▲" : "▼"}</span>
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: "#f0f4ff", borderBottom: "2px solid #dde6ff" }}>
          <td colSpan={4} style={{ padding: "12px 20px 14px 28px" }}>
            {r.isNote ? (
              <div style={{ fontSize: 11, color: "#555", fontStyle: "italic", lineHeight: 1.55, marginBottom: allPairs.length ? 12 : 0 }}>
                No action was taken — the rule evaluated but declined. {r.noteText}
              </div>
            ) : ACTION_DESC[r.action_type] && (
              <div style={{ fontSize: 11, color: "#555", fontStyle: "italic", lineHeight: 1.55, marginBottom: allPairs.length ? 12 : 0 }}>
                {ACTION_DESC[r.action_type]}
              </div>
            )}
            {allPairs.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 32px" }}>
                {allPairs.map(([k, v]) => (
                  <div key={k}>
                    <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>
                      {k.replace(/_/g, " ")}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#2c3e50", marginTop: 2, whiteSpace: "nowrap" }}>
                      {fmtMetaVal(k, v)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

function AdjustmentsLog({ frames, currentIdx }) {
  const [expanded, setExpanded] = useState(null);

  const rows = frames.slice(0, currentIdx + 1).flatMap(f => {
    const executed = (f.events || [])
      .filter(e => e.action_type)
      .map(e => ({
        date:        f.date,
        action_type: e.action_type,
        metadata:    e.metadata || {},
        pnl:         f.pnl?.cumulative || 0,
        isNote:      false,
      }));
    const considered = (f.rule_notes || []).map(n => ({
      date:        f.date,
      action_type: n.rule_name,
      metadata:    n.facts || {},
      noteText:    n.note,
      pnl:         f.pnl?.cumulative || 0,
      isNote:      true,
    }));
    return [...executed, ...considered];
  });

  return (
    <div style={{ ...panelCard, margin: "16px 0" }}>
      <div style={{ ...panelTitle, marginBottom: 12 }}>
        Adjustments Log
        <span style={{ fontWeight: 400, fontSize: 11, color: "#888", marginLeft: 8 }}>
          Executed trades + considered-but-declined evaluations, up to Day {currentIdx + 1} · click a row to expand
        </span>
      </div>
      {rows.length === 0 ? (
        <div style={{ color: "#bbb", fontSize: 12, textAlign: "center", padding: "16px 0" }}>
          No adjustments in this period yet.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                <th style={th}>Date</th>
                <th style={th}>Action</th>
                <th style={th}>Details</th>
                <th style={{ ...th, textAlign: "right" }}>Cumulative P&L</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <AdjLogRow
                  key={i}
                  r={r}
                  expanded={expanded === i}
                  onToggle={() => setExpanded(expanded === i ? null : i)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#6c757d", fontSize: 11, textTransform: "uppercase" };
const td = { padding: "7px 10px", color: "#333", verticalAlign: "middle" };

function dollar(n) {
  const abs = `$${Math.round(Math.abs(n)).toLocaleString()}`;
  return n < 0 ? `−${abs}` : n > 0 ? `+${abs}` : abs;
}

// ── Control button ────────────────────────────────────────────────────────────

function CtrlBtn({ onClick, children, primary, active, small, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: small ? "3px 8px" : "5px 12px",
        fontSize: small ? 11 : 12,
        fontWeight: 600,
        border: "1px solid",
        borderRadius: 6,
        cursor: disabled ? "default" : "pointer",
        background: primary ? "#3b82f6" : active ? "#e8f0fe" : "#fff",
        color:      primary ? "#fff"    : active ? "#1a56db" : "#495057",
        borderColor: primary ? "#3b82f6" : active ? "#93c5fd" : "#dee2e6",
        opacity: disabled ? 0.5 : 1,
        transition: "background 0.1s",
      }}
    >
      {children}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function BacktestReplay() {
  const { jobId }  = useParams();
  const history    = useHistory();

  const [loadStatus, setLoadStatus] = useState("LOADING"); // LOADING | POLLING | READY | FAILED
  const [jobMeta,    setJobMeta]    = useState(null);
  const [frames,     setFrames]     = useState([]);
  const [pollPct,    setPollPct]    = useState(0);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [playing,    setPlaying]    = useState(false);
  const [speed,      setSpeed]      = useState(1);
  const [err,        setErr]        = useState(null);

  const intervalRef = useRef(null);
  const pollRef     = useRef(null);

  // Load frames from completed job
  const loadFrames = useCallback(async () => {
    try {
      const r = await axios.get(`/backtest/frames/${jobId}`);
      setJobMeta({
        symbol:        r.data.symbol,
        strategy_type: r.data.strategy_type,
        start_date:    r.data.start_date,
        end_date:      r.data.end_date,
        expiry:        r.data.expiry,
      });
      setFrames(r.data.frames || []);
      setCurrentIdx(0);
      setLoadStatus("READY");
    } catch (e) {
      setErr("Failed to load frames: " + (e.response?.data?.error || e.message));
      setLoadStatus("FAILED");
    }
  }, [jobId]);

  // Initial load: check job status
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`/backtest/status/${jobId}`);
        const s = r.data.status;
        setPollPct(r.data.progress || 0);
        if (s === "COMPLETED") {
          await loadFrames();
        } else if (s === "FAILED") {
          setErr(r.data.error_message || "Backtest failed.");
          setLoadStatus("FAILED");
        } else {
          setLoadStatus("POLLING");
        }
      } catch (e) {
        setErr("Could not load backtest.");
        setLoadStatus("FAILED");
      }
    })();
  }, [jobId, loadFrames]);

  // Poll while PENDING / RUNNING
  useEffect(() => {
    if (loadStatus !== "POLLING") return;
    const poll = async () => {
      try {
        const r = await axios.get(`/backtest/status/${jobId}`);
        const s = r.data.status;
        setPollPct(r.data.progress || 0);
        if (s === "COMPLETED") {
          clearInterval(pollRef.current);
          loadFrames();
        } else if (s === "FAILED") {
          clearInterval(pollRef.current);
          setErr(r.data.error_message || "Backtest failed.");
          setLoadStatus("FAILED");
        }
      } catch (_) { /* keep polling */ }
    };
    pollRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollRef.current);
  }, [loadStatus, jobId, loadFrames]);

  // Playback ticker
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!playing || frames.length === 0) return;
    const ms = Math.round(1000 / speed);
    intervalRef.current = setInterval(() => {
      setCurrentIdx(i => {
        if (i >= frames.length - 1) { setPlaying(false); return i; }
        return i + 1;
      });
    }, ms);
    return () => clearInterval(intervalRef.current);
  }, [playing, speed, frames.length]);

  // ── Derived ──────────────────────────────────────────────────────────────

  const total        = frames.length;
  const currentFrame = frames[currentIdx] || null;
  const isAtStart    = currentIdx === 0;
  const isAtEnd      = currentIdx >= total - 1;
  const isPH         = ["PYRAMID_HEDGING", "FUTURES_PYRAMID_HEDGING"].includes(jobMeta?.strategy_type);
  const progressPct  = total > 0 ? ((currentIdx + 1) / total) * 100 : 0;

  // Stock % move (first vs current frame)
  const firstFrame   = frames[0];
  const stockMove    = firstFrame?.stock_ltp && currentFrame?.stock_ltp && currentIdx > 0
    ? ((currentFrame.stock_ltp - firstFrame.stock_ltp) / firstFrame.stock_ltp) * 100
    : null;

  // ── Loading states ────────────────────────────────────────────────────────

  if (loadStatus === "LOADING") {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Spinner /> <span style={{ marginLeft: 10, color: "#888" }}>Loading…</span>
      </div>
    );
  }

  if (loadStatus === "POLLING") {
    return (
      <div style={{ padding: 48, maxWidth: 480, margin: "0 auto" }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: "#2c3e50", marginBottom: 14 }}>
          Running Backtest…
        </div>
        <div style={{ height: 10, background: "#e9ecef", borderRadius: 5, overflow: "hidden" }}>
          <div style={{ height: "100%", background: "#3b82f6", borderRadius: 5, width: `${pollPct}%`, transition: "width 0.4s" }} />
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
          <Spinner size="sm" /> {pollPct}% complete — simulating in background…
        </div>
        <div style={{ marginTop: 16, fontSize: 12, color: "#999" }}>
          The full period is running. This page updates automatically when done.
        </div>
        <button onClick={() => history.push("/backtest")} style={backBtn}>← Back to Config</button>
      </div>
    );
  }

  if (loadStatus === "FAILED") {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <div style={{ color: "#dc3545", marginBottom: 16 }}>{err || "Backtest failed."}</div>
        <button onClick={() => history.push("/backtest")} style={backBtn}>← Back</button>
      </div>
    );
  }

  // ── Player ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: "0 0 32px 0" }}>

      {/* Header */}
      <div style={headerWrap}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18, fontWeight: 700, color: "#2c3e50" }}>{jobMeta.symbol}</span>
            <span style={stratBadge}>{strategyLabel(jobMeta.strategy_type)}</span>
            <span style={autoRunBadge}>Auto-Run</span>
          </div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 3 }}>
            {jobMeta.start_date} → {jobMeta.end_date}
            {jobMeta.expiry ? ` · Expiry ${jobMeta.expiry}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {total > 0 && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#2c3e50" }}>
                Day {currentIdx + 1} of {total}
              </div>
              <div style={{ fontSize: 11, color: "#888" }}>{currentFrame?.date || ""}</div>
            </div>
          )}
          {stockMove !== null && (
            <span style={{
              fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 12,
              background: stockMove >= 0 ? "#e8f5e9" : "#fde2e2",
              color: stockMove >= 0 ? "#1b5e20" : "#721c24",
            }}>
              {stockMove >= 0 ? "+" : ""}{stockMove.toFixed(1)}%
            </span>
          )}
          <button onClick={() => history.push("/backtest")} style={backBtn}>← Back</button>
        </div>
      </div>

      {/* Playback progress bar */}
      <div style={{ height: 4, background: "#e9ecef", marginBottom: 16 }}>
        <div style={{
          height: 4,
          background: isAtEnd ? "#28a745" : "#3b82f6",
          width: `${progressPct}%`,
          transition: "width 0.2s",
        }} />
      </div>

      {/* Summary card (shown when at end) */}
      {isAtEnd && frames.length > 0 && <SummaryCard frames={frames} />}

      {/* Main content grid */}
      <div style={mainGrid}>

        {/* Left panel */}
        <div>
          <div style={panelCard}>
            <div style={panelTitle}>Positions</div>
            <PositionsPanel frame={currentFrame} />
          </div>
          <div style={{ ...panelCard, marginTop: 12 }}>
            <div style={panelTitle}>P&L</div>
            <PnLBar frame={currentFrame} />
          </div>
          {isPH ? (
            <div style={{ ...panelCard, marginTop: 12 }}>
              <div style={panelTitle}>Ladder</div>
              <LadderView frame={currentFrame} />
            </div>
          ) : (
            <div style={{ ...panelCard, marginTop: 12 }}>
              <StrategyInfoPanel frame={currentFrame} strategyType={jobMeta?.strategy_type} />
            </div>
          )}
        </div>

        {/* Right panel */}
        <div>
          <div style={panelCard}>
            <div style={panelTitle}>
              {jobMeta.symbol} Price
              {currentFrame?.stock_ltp && (
                <span style={{ fontWeight: 400, color: "#888", marginLeft: 8 }}>
                  ${currentFrame.stock_ltp.toFixed(2)}
                </span>
              )}
              {(() => {
                const ebp = currentFrame?.strategy?.accounting?.ebp;
                const ltp = currentFrame?.stock_ltp;
                if (ebp == null || !ltp) return null;
                const breached = ltp < ebp;
                return (
                  <span
                    title="Effective Breakeven Price — the price the position is actually held at once every roll's realized premium is netted in. Below this, the position is underwater."
                    style={{
                      fontWeight: 600, marginLeft: 10, fontSize: 11,
                      color: breached ? "#dc3545" : "#28a745",
                      background: breached ? "#fde2e2" : "#e8f5e9",
                      borderRadius: 4, padding: "2px 6px",
                    }}
                  >
                    EBP ${ebp.toFixed(2)} {breached ? "· breached" : "· profitable"}
                  </span>
                );
              })()}
            </div>
            {frames.length > 0
              ? <StockPriceChart frames={frames} currentIndex={currentIdx} />
              : <div style={emptyChart}>No data</div>
            }
          </div>

          {frames.length > 1 && (
            <div style={{ ...panelCard, marginTop: 12 }}>
              <div style={panelTitle}>Cumulative P&L</div>
              <PnLChart frames={frames} currentIndex={currentIdx} />
            </div>
          )}

        </div>
      </div>

      {/* Adjustments log — all executed trades up to current frame */}
      <AdjustmentsLog frames={frames} currentIdx={currentIdx} />

      {/* Playback controls */}
      <div style={controlsBar}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <CtrlBtn onClick={() => { setPlaying(false); setCurrentIdx(0); }} disabled={isAtStart}>⏮</CtrlBtn>
          <CtrlBtn onClick={() => setCurrentIdx(i => Math.max(0, i - 1))} disabled={isAtStart}>◀</CtrlBtn>
          <CtrlBtn primary onClick={() => setPlaying(p => !p)} disabled={isAtEnd && !playing}>
            {playing ? "⏸ Pause" : "▶ Play"}
          </CtrlBtn>
          <CtrlBtn onClick={() => setCurrentIdx(i => Math.min(total - 1, i + 1))} disabled={isAtEnd}>▶</CtrlBtn>
          <CtrlBtn onClick={() => { setPlaying(false); setCurrentIdx(total - 1); }} disabled={isAtEnd}>⏭</CtrlBtn>

          <div style={{ width: 1, height: 20, background: "#dee2e6", margin: "0 6px" }} />

          {SPEEDS.map(s => (
            <CtrlBtn key={s.fps} active={speed === s.fps} small onClick={() => setSpeed(s.fps)}>
              {s.label}
            </CtrlBtn>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, marginLeft: 16 }}>
          <input
            type="range"
            min={0}
            max={Math.max(0, total - 1)}
            value={currentIdx}
            onChange={e => { setPlaying(false); setCurrentIdx(Number(e.target.value)); }}
            style={{ flex: 1, accentColor: "#3b82f6" }}
          />
          <span style={{ fontSize: 12, color: "#888", whiteSpace: "nowrap" }}>
            {currentFrame?.date || "—"}
          </span>
        </div>
      </div>

    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const headerWrap = {
  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  padding: "0 0 14px", borderBottom: "1px solid #e9ecef", marginBottom: 16,
};
const stratBadge = {
  fontSize: 11, fontWeight: 700, background: "#e8f5e9", color: "#1b5e20",
  border: "1px solid #81c784", padding: "2px 8px", borderRadius: 20,
};
const autoRunBadge = {
  fontSize: 11, fontWeight: 600, background: "#e3f2fd", color: "#1565c0",
  border: "1px solid #90caf9", padding: "2px 8px", borderRadius: 20,
};
const mainGrid = {
  display: "grid",
  gridTemplateColumns: "240px 1fr",
  gap: 14,
  alignItems: "start",
};
const panelCard = {
  background: "#fff", border: "1px solid #e9ecef", borderRadius: 8, padding: "12px 14px",
};
const panelTitle = {
  fontSize: 11, fontWeight: 700, color: "#6c757d",
  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10,
};
const controlsBar = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  background: "#fff", border: "1px solid #e9ecef", borderRadius: 8,
  padding: "12px 16px", marginTop: 16,
};
const summaryCard = {
  background: "linear-gradient(135deg, #e8f5e9 0%, #f0f9ff 100%)",
  border: "1.5px solid #81c784",
  borderRadius: 10, padding: "16px 20px", marginBottom: 16,
};
const emptyChart = {
  height: 120, display: "flex", alignItems: "center", justifyContent: "center",
  color: "#bbb", fontSize: 12,
};
const backBtn = {
  background: "none", border: "1px solid #dee2e6", borderRadius: 6,
  padding: "5px 12px", fontSize: 12, color: "#6c757d", cursor: "pointer", marginTop: 16,
};
